import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserProfile } from '../utils/api';
import './UserProfile.css';

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const userEmail = localStorage.getItem('userEmail') || 'User';

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true);
    setError('');

    try {
      // Get user_id from localStorage or use a default/fallback
      const userId = localStorage.getItem('userId') || localStorage.getItem('userEmail') || 'default';
      const data = await getUserProfile(userId);
      console.log(data.user.profile);
      setProfile(data.user.profile);
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    navigate('/login');
  };

  // Format field name from camelCase or snake_case to Title Case
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
      .trim();
  };

  // Check if value is a URL
  const isUrl = (value) => {
    if (typeof value !== 'string') return false;
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (e) {
      return false;
    }
  };

  // Format value based on type
  const formatValue = (value, returnAsJSX = false) => {
    if (value === null || value === undefined) return null;
    
    // Handle URLs
    if (isUrl(value)) {
      if (returnAsJSX) {
        return (
          <a href={value} target="_blank" rel="noopener noreferrer" className="url-link">
            Click here to see 🔗
          </a>
        );
      }
      return value; // Return raw value for non-JSX contexts
    }
    
    // Check if it's a date string and format it
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        return new Date(value).toLocaleDateString();
      } catch (e) {
        return value;
      }
    }
    
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // Handle arrays (only primitive values, not objects)
    if (Array.isArray(value) && !isArrayOfObjects(value)) {
      return value.join(', ');
    }
    
    return String(value);
  };

  // Check if value is a complex object (not primitive)
  const isComplexObject = (value) => {
    return value !== null && 
           typeof value === 'object' && 
           !Array.isArray(value) &&
           Object.keys(value).length > 0;
  };

  // Check if value is an array of objects
  const isArrayOfObjects = (value) => {
    return Array.isArray(value) && 
           value.length > 0 && 
           value.some(item => typeof item === 'object' && item !== null && !Array.isArray(item));
  };

  // Check if value should be rendered
  const shouldRenderValue = (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
    return true;
  };

  const renderProfileField = (label, value) => {
    if (!shouldRenderValue(value)) return null;
    
    const formattedValue = formatValue(value, true);
    if (!formattedValue) return null;
    
    return (
      <div key={label} className="profile-field">
        <label className="field-label">{label}</label>
        <div className="field-value">{formattedValue}</div>
      </div>
    );
  };

  // Render all fields from an object dynamically
  const renderObjectFields = (obj, excludeFields = []) => {
    if (!obj) return null;
    
    return Object.entries(obj)
      .filter(([key]) => !excludeFields.includes(key))
      .map(([key, value]) => {
        if (isComplexObject(value) || isArrayOfObjects(value)) return null; // Skip complex objects and arrays of objects
        return renderProfileField(formatFieldName(key), value);
      })
      .filter(Boolean);
  };

  // Get complex objects from profile
  const getComplexObjects = (obj, excludeFields = []) => {
    if (!obj) return {};
    
    const complexObjs = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (!excludeFields.includes(key) && isComplexObject(value)) {
        complexObjs[key] = value;
      }
    });
    return complexObjs;
  };

  // Get arrays of objects from profile
  const getArraysOfObjects = (obj, excludeFields = []) => {
    if (!obj) return {};
    
    const arrayObjs = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (!excludeFields.includes(key) && isArrayOfObjects(value)) {
        arrayObjs[key] = value;
      }
    });
    return arrayObjs;
  };

  // Render an array of objects as cards
  const renderArrayOfObjects = (sectionKey, arrayData) => {
    if (!arrayData || arrayData.length === 0) return null;

    return (
      <div key={sectionKey} className="profile-details">
        <h3 className="section-title">{formatFieldName(sectionKey)}</h3>
        <div className="array-items-container">
          {arrayData.map((item, index) => {
            if (typeof item !== 'object' || item === null) return null;
            
            const fields = Object.entries(item).map(([key, value]) => {
              if (!shouldRenderValue(value)) return null;
              
              return (
                <div key={key} className="array-item-field">
                  <span className="array-field-label">{formatFieldName(key)}:</span>
                  <span className="array-field-value">{formatValue(value, true)}</span>
                </div>
              );
            }).filter(Boolean);

            if (fields.length === 0) return null;

            return (
              <div key={index} className="array-item-card">
                <div className="array-item-header">Item {index + 1}</div>
                <div className="array-item-content">
                  {fields}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div>
          <h1 className="profile-title">User Profile</h1>
          <p className="profile-subtitle">{userEmail}</p>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate('/upload')} className="nav-button">
            📁 Upload Files
          </button>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="profile-content">
        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading profile...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p className="error-message">{error}</p>
            <button onClick={fetchUserProfile} className="retry-button">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && profile && (
          <div className="profile-card">
            <div className="profile-avatar">
              {profile.photo_url ? (
                <img 
                  src={profile.photo_url} 
                  alt="Profile" 
                  className="avatar-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="avatar-circle" style={{ display: profile.photo_url ? 'none' : 'flex' }}>
                {profile.name ? profile.name.charAt(0).toUpperCase() : userEmail.charAt(0).toUpperCase()}
              </div>
            </div>

            <div className="profile-info">
              <h2 className="profile-name">{profile.name || profile.fullName || profile["Full Name"] || 'User Name'}</h2>
              <p className="profile-email">{profile.email || userEmail}</p>
            </div>

            {/* Render all simple fields dynamically */}
            {(() => {
              const simpleFields = renderObjectFields(profile);
              if (simpleFields.length === 0) return null;
              
              return (
                <div className="profile-details">
                  <h3 className="section-title">Profile Information</h3>
                  <div className="details-grid">
                    {simpleFields}
                  </div>
                </div>
              );
            })()}

            {/* Render complex nested objects dynamically */}
            {(() => {
              const complexObjects = getComplexObjects(profile);
              
              return Object.entries(complexObjects).map(([sectionKey, sectionData]) => {
                const fields = renderObjectFields(sectionData);
                if (fields.length === 0) return null;
                
                return (
                  <div key={sectionKey} className="profile-details">
                    <h3 className="section-title">{formatFieldName(sectionKey)}</h3>
                    <div className="details-grid">
                      {fields}
                    </div>
                  </div>
                );
              });
            })()}

            {/* Render arrays of objects dynamically */}
            {(() => {
              const arraysOfObjects = getArraysOfObjects(profile);
              
              return Object.entries(arraysOfObjects).map(([sectionKey, arrayData]) => {
                return renderArrayOfObjects(sectionKey, arrayData);
              });
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;

